/**
 * Applying settings to the running game (plan G12). Mouse sensitivity and invert Y mutate the runtime
 * bindings (`ctx.data.bindings`, which the input layer reads on every event); the multiplier and the flip are
 * relative to the values bindings.json shipped with, captured once, so repeated applies never compound.
 * Default FA is applied whenever the player (re)spawns; master volume and screen shake are read by their
 * features through `ctx.settings`.
 *
 * Settings › Interface (spec 2026-10-09 §5.5–5.6, card F2) apply to the page root: the UI scale as the
 * `--ui-user-scale` custom property, text contrast and reduced motion as classes. The tokens card (F1) owns the CSS
 * that reads them; these names are the contract between the two.
 */
import type { ReducedMotionMode, Settings } from '../app/feature'
import { EV, eventSlot, firstReadable, type EventRing } from '../combat/events'
import type { Bindings } from '../data/schemas'

/** The bindings.json values the settings are relative to. */
export interface InputBase {
  readonly sensitivity: number
  readonly mouseInvertY: boolean
  readonly padInvertPitch: boolean
}

export function captureInputBase(bindings: Bindings): InputBase {
  return {
    sensitivity: bindings.mouse.sensitivity,
    mouseInvertY: bindings.mouse.invertY,
    padInvertPitch: bindings.gamepad.invert.pitch,
  }
}

export function applyInputSettings(bindings: Bindings, base: InputBase, settings: Settings): void {
  bindings.mouse.sensitivity = base.sensitivity * settings.mouseSensitivity
  bindings.mouse.invertY = base.mouseInvertY !== settings.invertY
  bindings.gamepad.invert.pitch = base.padInvertPitch !== settings.invertY
}

/** True when the ring holds a `spawn` or `respawn` of `playerEid` from `cursor` on. Allocation-free. */
export function playerSpawnedSince(ring: EventRing, cursor: number, playerEid: number): boolean {
  if (playerEid < 0) return false
  let found = false
  for (let i = firstReadable(ring, cursor); i < ring.head; i++) {
    const s = eventSlot(i)
    const t = ring.type[s]
    if ((t === EV.spawn || t === EV.respawn) && ring.eid[s] === playerEid) found = true
  }
  return found
}

/** The custom property on <html> that scales the root font size (spec §5.5). */
export const UI_SCALE_VAR = '--ui-user-scale'
/** Classes on <html> for the High contrast theme (§5.1) and reduced motion (§5.3). */
export const UI_CLASSES = Object.freeze({ highContrast: 'ui-high-contrast', reducedMotion: 'ui-reduced-motion' })

/** The parts of `document.documentElement` the Interface settings touch (tests pass a fake). */
export interface UiRoot {
  readonly style: { setProperty(name: string, value: string): void }
  readonly classList: { toggle(token: string, force: boolean): unknown }
}

/** Reduced motion is on when set On, or when it follows the OS and the OS asks for it. */
export function reducedMotionActive(mode: ReducedMotionMode, osPrefersReduced: boolean): boolean {
  return mode === 'on' || (mode === 'system' && osPrefersReduced)
}

/** Applies UI scale, text contrast and reduced motion to the page root. Idempotent; UI events only. */
export function applyInterfaceSettings(root: UiRoot, settings: Settings, osPrefersReduced: boolean): void {
  root.style.setProperty(UI_SCALE_VAR, String(settings.uiScale))
  root.classList.toggle(UI_CLASSES.highContrast, settings.textContrast === 'high')
  root.classList.toggle(UI_CLASSES.reducedMotion, reducedMotionActive(settings.reducedMotion, osPrefersReduced))
}

/** Dashboard time (spec §1.3): true when opening the dashboard should hold the sim (Pause, the default). */
export function dashboardHoldsSim(settings: Settings): boolean {
  return settings.dashboardTime === 'pause'
}
